import { useEffect, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import DOMPurify from 'dompurify';

// Reply's DAM doesn't send CORS headers, so its SVGs can only be fetched for
// inspection (see below) via the backend proxy configured in app-config.yaml.
const DAM_MEDIA_HOST = 'dam-media.reply.com';

// Breathing room added back around the tight content bbox so the mark
// doesn't touch the container edges.
const VIEWBOX_PADDING_RATIO = 0.04;

// Module-level so repeated entities sharing a logo (e.g. every Component in
// a Tenant) only fetch and crop it once per page session.
const croppedMarkupCache = new Map<string, string | null>();

function isSvgUrl(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().endsWith('.svg');
  } catch {
    return false;
  }
}

function resolveFetchUrl(url: string, proxyBaseUrl: string): string {
  const parsed = new URL(url);
  if (parsed.hostname === DAM_MEDIA_HOST) {
    return `${proxyBaseUrl}/dam-media${parsed.pathname}${parsed.search}`;
  }
  return url;
}

/**
 * Many brand SVGs are exported on an oversized artboard (e.g. a letterhead
 * canvas) with the actual mark occupying only part of it. object-fit:
 * contain has no way to know that, so it "maximizes" the whole canvas
 * including its blank space. This measures the real rendered bounding box
 * of the artwork and rewrites the viewBox to match, so the mark itself
 * fills the container.
 */
function cropSvgMarkupToContent(rawMarkup: string): string | null {
  const sanitized = DOMPurify.sanitize(rawMarkup, {
    USE_PROFILES: { svg: true, svgFilters: true },
  });

  const doc = new DOMParser().parseFromString(sanitized, 'image/svg+xml');
  const svg = doc.documentElement;

  if (svg.nodeName !== 'svg' || doc.querySelector('parsererror')) {
    return null;
  }

  // getBBox() only reflects reality once the element is part of a rendered
  // (even if invisible) document tree.
  const host = document.createElement('div');
  host.style.position = 'absolute';
  host.style.width = '0';
  host.style.height = '0';
  host.style.overflow = 'hidden';
  host.style.visibility = 'hidden';
  document.body.appendChild(host);
  host.appendChild(svg);

  let bbox: DOMRect | null = null;
  try {
    bbox = (svg as unknown as SVGGraphicsElement).getBBox();
  } catch {
    bbox = null;
  } finally {
    document.body.removeChild(host);
  }

  if (!bbox || bbox.width <= 0 || bbox.height <= 0) {
    return null;
  }

  const paddingX = bbox.width * VIEWBOX_PADDING_RATIO;
  const paddingY = bbox.height * VIEWBOX_PADDING_RATIO;

  svg.setAttribute(
    'viewBox',
    [
      bbox.x - paddingX,
      bbox.y - paddingY,
      bbox.width + paddingX * 2,
      bbox.height + paddingY * 2,
    ].join(' '),
  );
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  return svg.outerHTML;
}

/**
 * Fetches an entity logo's SVG, crops its viewBox to the actual artwork
 * bounds, and returns sanitized markup ready for dangerouslySetInnerHTML.
 * Returns null while loading, on any failure, or for non-SVG URLs — callers
 * should fall back to a plain <img> in that case.
 */
export function useCroppedSvgLogo(logoUrl: string | undefined): string | null {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [croppedMarkup, setCroppedMarkup] = useState<string | null>(null);

  useEffect(() => {
    setCroppedMarkup(null);

    if (!logoUrl || !isSvgUrl(logoUrl)) {
      return undefined;
    }

    if (croppedMarkupCache.has(logoUrl)) {
      setCroppedMarkup(croppedMarkupCache.get(logoUrl) ?? null);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const proxyBaseUrl = await discoveryApi.getBaseUrl('proxy');
        const response = await fetchApi.fetch(
          resolveFetchUrl(logoUrl, proxyBaseUrl),
        );
        if (!response.ok) {
          throw new Error(`Logo request failed (${response.status})`);
        }
        const rawMarkup = await response.text();
        const cropped = cropSvgMarkupToContent(rawMarkup);
        croppedMarkupCache.set(logoUrl, cropped);
        if (!cancelled) {
          setCroppedMarkup(cropped);
        }
      } catch {
        // CORS-blocked, unreachable, or malformed — fall back to <img>.
        croppedMarkupCache.set(logoUrl, null);
        if (!cancelled) {
          setCroppedMarkup(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [logoUrl, discoveryApi, fetchApi]);

  return croppedMarkup;
}
